import { IoMdClose } from "react-icons/io";
import {
  Modal,
  Typography,
  Select,
  Button,
  MultiSelect,
  DateInput,
  Tag,
} from "components";
import {
  getAllTransactionsData,
  getTransactionDetails,
  setTransactionQuery,
  searchAgent,
  setSearchedAgentValue,
} from "redux/actions/transactionActions";
import AsyncSelect from "react-select/async";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AiOutlineCheck } from "react-icons/ai";
import {
  getUniqueTransactionTypes,
  isAgentUser,
  formatUrlQuery,
  isAdminUser,
  isAggregatorUser,
} from "utils/helper";
import { getTerminals } from "redux/actions/terminalAction";
import { getTerminalActivity } from "redux/actions/dashboard/terminalDashboardActions";
import debounce from "lodash.debounce";

const TransactionFilterModal = ({ isFilterVisible, setIsFilterVisible }) => {
  const {
    user,
    terminals,
    loading,
    regions,
    transactionTypes,
    transactionQuery,
    searchedAgent,
  } = useSelector((state) => ({
    regions: state.regions.regions,
    user: state.user.user,
    terminals: state.terminalReducer.terminals,
    loading: state.transactionsReducer.loading,
    transactionTypes: state.transactionsReducer.transactionTypes,
    transactionQuery: state.transactionsReducer.params,
    searchedAgent: state.transactionsReducer.searchedAgent,
  }));

  const dispatch = useDispatch();

  const [agentsToDisplay, setAgentsToDisplay] = useState({
    agents: [],
    loading: false,
  });

  useEffect(() => {
    //Make sure to only fetch user's terminals when an aggregator or admin has selected an agentId
    if (!isAgentUser(user) && transactionQuery.agentId) {
      const requestParams = {
        agentId: transactionQuery.agentId,
        status: "ENABLED",
        size: 50,
      };
      dispatch(getTerminals(requestParams));
    }
  }, [dispatch, transactionQuery.agentId, user]);

  const showTerminalAndPmNumber = () => {
    if (isAgentUser(user)) return true;

    return !!transactionQuery.agentId; //Return true if Logged in user is not agent and has selected agent to view
  };

  const agentsQueryParams = {
    size: 100,
    parentId: isAdminUser(user) ? undefined : user?.agent_id,
    sorted: true,
  };

  useEffect(() => {
    const getAggregatorAgents = async () => {
      setAgentsToDisplay({
        ...agentsToDisplay,
        loading: true,
      });
      try {
        const { data } = await makeRequest(
          "get",
          routes.getAgentPersonalDetails(formatUrlQuery(agentsQueryParams))
        );
        const aggregatorAgents = data.data?.content.map(
          (agent) =>
            `${agent?.agentDetails?.agentId} - ${agent?.lastName} ${agent?.firstName}`
        );

        setAgentsToDisplay({
          ...agentsToDisplay,
          agents: [...agentsToDisplay.agents, ...aggregatorAgents],
          loading: false,
        });
      } catch (error) {
        setAgentsToDisplay({
          ...agentsToDisplay,
          loading: false,
        });
      }
    };
    if (isAggregatorUser(user)) getAggregatorAgents();
    //eslint-disable-next-line
  }, []);

  const statuses = [
    {
      text: "Completed",
      value: "COMPLETED",
      textColor: "#27AE60",
      backgroundColor: "#EAFCF2",
    },
    {
      text: "Pending-Hold",
      value: "PENDING_HOLD",
      textColor: "#0C1575",
      backgroundColor: "#E4EAFD",
    },
    {
      text: "Pending-Agent",
      value: "PENDING_CREDIT_AGENT",
      textColor: "#1966FC",
      backgroundColor: "#E4EAFD",
    },
    {
      text: "Pending",
      value: "PENDING",
      textColor: "#EFB008",
      backgroundColor: "#FEF4DB",
    },
    {
      text: "Failed",
      value: "FAILED",
      textColor: "#DC2C2C",
      backgroundColor: "#FFE6E6",
    },
  ];

  const handleStatusClick = (selectedStatus) => {
    let selectedStatuses = transactionQuery.status;
    const statusIndex = selectedStatuses.indexOf(selectedStatus);
    if (statusIndex > -1) {
      selectedStatuses.splice(statusIndex, 1);
    } else {
      selectedStatuses = [...selectedStatuses, selectedStatus];
    }
    setTransactionQuery({
      name: "status",
      value: selectedStatuses,
    });
  };

  const handleAllStatusClick = () => {
    if (transactionQuery.status.length === statuses.length) {
      setTransactionQuery({
        name: "status",
        value: [],
      });
    } else {
      setTransactionQuery({
        name: "status",
        value: statuses.map((status) => status.value),
      });
    }
  };

  const terminalsToDisplay = terminals.length
    ? [
        { label: "All Terminals", value: "" },
        ...terminals.map((terminal) => ({
          label: `Terminal ${terminal.terminalId} - SN/${terminal.serialNumber}`,
          value: terminal.terminalId,
        })),
      ]
    : [{ label: "No Active terminal", value: "" }];

  const agentSearchStyles = {
    control: (base) => ({
      ...base,
      width: "100%",
    }),
    placeholder: (base) => ({ ...base, fontSize: "12px" }),
  };

  const _loadSuggestions = (agentId, callback) => {
    searchAgent(agentId).then((resp) => callback(resp));
  };

  const loadSuggestions = debounce(_loadSuggestions, 1000);

  return (
    <Modal
      showModal={isFilterVisible}
      customPadding={"0"}
      onClose={() => setIsFilterVisible(false)}
    >
      <div className="transaction-filter-modal">
        <div className="header">
          <div className="d-flex justify-content-end ">
            <IoMdClose
              className="cursor-pointer"
              onClick={() => setIsFilterVisible(false)}
            />
          </div>
          <Typography variant="body0" state="primary" className={"text-center"}>
            Transaction Details
          </Typography>
          <Typography variant="caption0" className="text-center">
            Use search filters to narrow down the results displayed
          </Typography>
        </div>
        <div className="body p-4">
          <div className="row">
            {isAdminUser(user) ||
              (isAggregatorUser(user) && (
                <div className="col-12">
                  <Typography>Search Agent</Typography>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadSuggestions}
                    styles={agentSearchStyles}
                    value={searchedAgent}
                    placeholder="Search with Agent Code, ID or Name"
                    onChange={(val) => {
                      setSearchedAgentValue(val || {});
                      setTransactionQuery({
                        name: "agentId",
                        value: val?.value || "",
                      });

                      getAllTransactionsData();
                      getTerminalActivity(val?.value || "");
                    }}
                    loadingMessage={() => "Searching Agents..."}
                    isClearable
                  />
                </div>
              ))}

            <div className="col-xs-12 col-md-6 transaction-types-container">
              <Typography>Transaction Type</Typography>
              <div>
                <MultiSelect
                  placeholder={"All Transactions"}
                  className=""
                  value={transactionQuery.type}
                  isSearchable={false}
                  clearable={false}
                  handleChange={(val) => {
                    setTransactionQuery({
                      name: "type",
                      value: val,
                    });

                    getAllTransactionsData();
                  }}
                  options={getUniqueTransactionTypes(transactionTypes)?.map(
                    ({ type, description }) => ({
                      label: description,
                      value: type,
                    })
                  )}
                  name="transaction-type-select"
                  borderStyle={"none"}
                />
                <div className="d-flex justify-content-end flex-wrap mb-2 selected-transactions">
                  {transactionQuery.type.length ===
                    getUniqueTransactionTypes(transactionTypes)?.length ||
                  !transactionQuery.type.length ? (
                    <Typography>All Transactions</Typography>
                  ) : (
                    transactionQuery.type.map((transaction, i) => (
                      <Typography key={i}>
                        {transaction.label}
                        {i === transactionQuery.type.length - 1
                          ? "."
                          : ","}{" "}
                        &nbsp;
                      </Typography>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-md-6">
              <Typography>Transaction Mode</Typography>
              <Select
                placeholder="All"
                required={false}
                options={[
                  {
                    label: "All",
                    value: "",
                  },
                  {
                    label: "Cash",
                    value: "CASH",
                  },
                  {
                    label: "Card",
                    value: "CARD",
                  },
                ]}
                value={transactionQuery.transMode}
                onChange={(e) => {
                  setTransactionQuery({
                    name: "transMode",
                    value: e.target.value,
                  });
                  getTransactionDetails();
                }}
              />
            </div>
            {showTerminalAndPmNumber() && (
              <>
                <div className="col-xs-12 col-md-6">
                  <Typography>Terminal ID</Typography>
                  <Select
                    required={false}
                    placeholder="All terminals"
                    options={terminalsToDisplay}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTransactionQuery({
                        name: "pmNumber",
                        value: "",
                      });
                      setTransactionQuery({
                        name: "terminalId",
                        value,
                      });

                      getAllTransactionsData();
                    }}
                    value={transactionQuery.terminalId}
                  />
                </div>

                <div className="col-xs-12 col-md-6">
                  <Typography>PM Number</Typography>
                  <Select
                    required={false}
                    disabled={!transactionQuery.terminalId}
                    options={terminals
                      .find(
                        (terminal) =>
                          terminal.terminalId === transactionQuery.terminalId
                      )
                      ?.pmNumber?.map((number) => ({
                        label: number,
                        value: number,
                      }))}
                    onChange={(e) => {
                      setTransactionQuery({
                        name: "pmNumber",
                        value: e.target.value,
                      });
                    }}
                    value={transactionQuery.pmNumber}
                  />
                </div>
              </>
            )}

            <div className="col-xs-12 col-md-6">
              <Typography>Date From</Typography>
              <DateInput
                className="date-select"
                name={"dateFrom"}
                value={transactionQuery.dateFrom}
                onChange={(val) => {
                  setTransactionQuery({
                    name: "dateFrom",
                    value: val,
                  });
                }}
                size="sm"
                maxDate={new Date()}
              />
            </div>
            <div className="col-xs-12 col-md-6">
              <Typography>Date To</Typography>
              <DateInput
                className="date-select"
                name={"dateTo"}
                value={transactionQuery.dateTo}
                onChange={(val) => {
                  setTransactionQuery({
                    name: "dateTo",
                    value: val,
                  });
                }}
                size="sm"
                maxDate={new Date()}
              />
            </div>
            {isAdminUser(user) && (
              <div className="col-xs-12 ">
                <Typography>Processor</Typography>
                <Select
                  placeholder="All"
                  required={false}
                  options={[
                    {
                      label: "All",
                      value: "",
                    },
                    {
                      label: "INTERSWITCH",
                      value: "INTERSWITCH",
                    },
                    {
                      label: "CORAL",
                      value: "CORAL",
                    },
                    {
                      label: "NIBSS",
                      value: "NIBSS",
                    },
                  ]}
                  value={transactionQuery.processor}
                  onChange={(e) => {
                    setTransactionQuery({
                      name: "processor",
                      value: e.target.value,
                    });
                  }}
                />
              </div>
            )}
            {!isAgentUser(user) && (
              <div>
                <Typography>Regions</Typography>
                <MultiSelect
                  placeholder={"All Regions"}
                  isSearchable={false}
                  clearable={false}
                  value={transactionQuery.region}
                  options={regions.map((item) => ({
                    label: item.name,
                    value: item.regionCode,
                  }))}
                  handleChange={(val) =>
                    setTransactionQuery({
                      name: "region",
                      value: val,
                    })
                  }
                  borderStyle="1.5px solid #E4EAFD"
                  name="region-select"
                />{" "}
                <div className="d-flex justify-content-end flex-wrap mb-2 selected-transactions">
                  {transactionQuery.region?.length === regions?.length ||
                  !transactionQuery.region?.length ? (
                    <Typography>All Regions</Typography>
                  ) : (
                    transactionQuery.region?.map((region, i) => (
                      <Typography key={i}>
                        {region.label}
                        {i === transactionQuery.region?.length - 1
                          ? "."
                          : ","}{" "}
                        &nbsp;
                      </Typography>
                    ))
                  )}
                </div>
              </div>
            )}
            <div>
              <Typography>Select Transaction Status(es)</Typography>
              <div className="status-selection-container">
                <div
                  className="tag-container"
                  onClick={() => handleAllStatusClick()}
                >
                  <Tag
                    text="All Statuses"
                    backgroundColor="#F9F9F9"
                    textColor="#778CA2"
                    style={{ height: "28px", cursor: "pointer" }}
                  />
                </div>

                <div className="other-statuses">
                  {statuses.map((prop, key) => (
                    <div
                      className="tag-container"
                      key={key}
                      onClick={() => handleStatusClick(prop.value)}
                    >
                      {transactionQuery?.status?.includes(prop.value) && (
                        <div className="check-icon">
                          <AiOutlineCheck color={prop.textColor} />
                        </div>
                      )}

                      <Tag
                        key={key}
                        {...prop}
                        style={{
                          minWidth: "75px",
                          height: "28px",
                          cursor: "pointer",
                          fontSize: "4px",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="footer d-flex justify-content-center align-items-center pb-4">
        <Button
          label="Cancel"
          variant="secondary"
          size="xs"
          onClick={() => setIsFilterVisible(false)}
        />{" "}
        &nbsp; &nbsp;
        <Button
          label="Apply Filter"
          size="xs"
          loading={loading}
          onClick={() =>
            getAllTransactionsData(() => setIsFilterVisible(false))
          }
        />
      </div>
    </Modal>
  );
};

export default TransactionFilterModal;
